import { z } from 'zod';
import { PackageType, MappingStatus } from '../../domain/entities/PackageMapping';

/**
 * Package Mapping Request DTOs
 * 
 * Zod schemas for validating package mapping API requests.
 * These schemas ensure type safety and data integrity at the API boundary.
 */

export const CreatePackageMappingRequestSchema = z.object({
    packageName: z.string().min(1, 'Package name is required'),
    packageType: z.nativeEnum(PackageType),
    domainId: z.string().uuid().optional(),
    confidence: z.number().min(0).max(1).optional(),
    reasoning: z.string().optional(),
    automaticallyClassified: z.boolean().default(false),
    notes: z.string().optional()
});

export const UpdatePackageMappingRequestSchema = z.object({
    domainId: z.string().uuid().optional(),
    status: z.nativeEnum(MappingStatus).optional(),
    notes: z.string().optional(),
    reviewedBy: z.string().optional(),
    confidence: z.number().min(0).max(1).optional(),
    reasoning: z.string().optional()
});

export const BulkPackageMappingRequestSchema = z.object({
    mappings: z.array(z.object({
        packageName: z.string().min(1),
        domainId: z.string().uuid()
    })).min(1, 'At least one mapping is required')
});

export const GetPackageMappingsQuerySchema = z.object({
    domainId: z.string().uuid().optional(),
    status: z.nativeEnum(MappingStatus).optional(),
    packageType: z.nativeEnum(PackageType).optional(),
    limit: z.coerce.number().min(1).max(100).default(50),
    offset: z.coerce.number().min(0).default(0),
    search: z.string().optional()
});

// Export types for use in application layer
export type CreatePackageMappingRequest = z.infer<typeof CreatePackageMappingRequestSchema>;
export type UpdatePackageMappingRequest = z.infer<typeof UpdatePackageMappingRequestSchema>;
export type BulkPackageMappingRequest = z.infer<typeof BulkPackageMappingRequestSchema>;
export type GetPackageMappingsQuery = z.infer<typeof GetPackageMappingsQuerySchema>;